import { App, Button, Form, Radio, Select, Space } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AlertDismissTypeEnum } from '../../../enums/AlertDismissTypeEnum';
import type ActiveAlertItem from '../../../interfaces/ActiveAlertItem';
import { useAppStore } from '../../../store/store';
import { formatSnoozeHoursLabel } from '../../../utils/alertMessages';

export interface AlertDismissFormProps {
  alert: ActiveAlertItem;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * AlertDismissForm
 *
 * This form lets users dismiss (permanently or temporarily/snooze) a server-driven alert.
 * It supports two actions, depending on the server configuration:
 *   - Permanent dismiss
 *   - Snooze for a predetermined number of hours
 * Calls POST /alerts/:code/dismiss under the hood via dismissAlertRequest from the zustand store.
 */
export default function AlertDismissForm({ alert, onSuccess, onCancel }: AlertDismissFormProps) {
  // Translation hook for i18n labels
  const { t } = useTranslation();
  // Success: message (toast); errors: notification (as elsewhere in the app)
  const { message, notification } = App.useApp();
  // Zustand store action for dismissing an alert
  const dismissAlertRequest = useAppStore((s) => s.dismissAlertRequest);
  // Button loading state
  const [submitting, setSubmitting] = useState(false);

  // Whether this alert supports permanent dismissing
  const hasPermanent = alert.dismissOptions.includes(AlertDismissTypeEnum.Permanent);
  // Whether this alert supports snooze (temporary dismiss)
  const hasSnooze = alert.dismissOptions.includes(AlertDismissTypeEnum.Snooze);

  // Default action: if snooze supported, default to snooze, else permanent
  const defaultType = useMemo(() => {
    if (hasSnooze) {
      return AlertDismissTypeEnum.Snooze;
    }
    return AlertDismissTypeEnum.Permanent;
  }, [hasSnooze]);

  // Current selected action (permanent or snooze)
  const [dismissType, setDismissType] = useState<AlertDismissTypeEnum>(defaultType);
  // Snooze duration, controlled by Select. Defaults to first available option, or null.
  const [snoozeHours, setSnoozeHours] = useState<number | null>(() =>
    alert.snoozeDurations?.length ? alert.snoozeDurations[0]! : null,
  );

  // Options for snooze durations, formatted with translation
  const snoozeOptions = useMemo(
    () =>
      (alert.snoozeDurations ?? []).map((h) => ({
        label: formatSnoozeHoursLabel(h, t),
        value: h,
      })),
    [alert.snoozeDurations, t],
  );

  /**
   * Should show snooze select dropdown if:
   *   - snooze is enabled
   *   - there is at least one snooze option
   *   - if both permanent & snooze are enabled, only show if snooze is chosen
   *   - otherwise, show if snooze is the only option
   */
  const showSnoozeSelect =
    hasSnooze &&
    snoozeOptions.length > 0 &&
    (!hasPermanent || dismissType === AlertDismissTypeEnum.Snooze);

  /**
   * onSubmit
   *
   * Sends the dismiss request. Handles both permanent and snooze logic:
   * - validates snooze duration selection
   * - disables the form while submitting
   * - on success: shows message.success and calls onSuccess
   * - on failure: shows error notification
   */
  const handleSubmit = async (): Promise<void> => {
    // Validate for snooze: snoozeHours must be set and valid
    if (dismissType === AlertDismissTypeEnum.Snooze) {
      if (snoozeHours == null || !alert.snoozeDurations.includes(snoozeHours)) {
        notification.error({ title: t('errors.alerts.invalidSnooze') });
        return;
      }
    }

    setSubmitting(true);
    try {
      if (dismissType === AlertDismissTypeEnum.Permanent) {
        // Trigger permanent dismiss
        await dismissAlertRequest(alert.code, { dismissType: AlertDismissTypeEnum.Permanent });
      } else {
        // Trigger snooze dismiss with selected duration
        await dismissAlertRequest(alert.code, {
          dismissType: AlertDismissTypeEnum.Snooze,
          snoozeHours: snoozeHours!,
        });
      }
      message.success(t('modal.alertDismiss.success'));
      onSuccess();
    } catch {
      // Generic error feedback
      notification.error({ title: t('errors.alerts.dismissFailed') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 py-2">
      {/* Only show action choice if both options are present */}
      {hasPermanent && hasSnooze ? (
        <Form layout="vertical" className="mb-0!">
          <Form.Item label={t('modal.alertDismiss.chooseAction')}>
            <Radio.Group
              value={dismissType}
              onChange={(e) => setDismissType(e.target.value as AlertDismissTypeEnum)}
            >
              <Radio value={AlertDismissTypeEnum.Permanent}>
                {t('modal.alertDismiss.permanent')}
              </Radio>
              <Radio value={AlertDismissTypeEnum.Snooze}>{t('modal.alertDismiss.snooze')}</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      ) : null}

      {/* Show snooze select if applicable (see logic above) */}
      {showSnoozeSelect ? (
        <Form layout="vertical" className="mb-0!">
          <Form.Item label={t('modal.alertDismiss.snoozeDuration')}>
            <Select
              className="w-full"
              options={snoozeOptions}
              value={snoozeHours ?? undefined}
              onChange={(v) => setSnoozeHours(v)}
            />
          </Form.Item>
        </Form>
      ) : null}

      {/* Footer actions: Cancel and Confirm buttons */}
      <Space className="flex w-full justify-end">
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="primary" loading={submitting} onClick={() => void handleSubmit()}>
          {t('modal.alertDismiss.confirm')}
        </Button>
      </Space>
    </div>
  );
}
